import { useState } from "react"
import { messageFromError } from "../utils/error"
import { useStateWithGetter } from "./useStateWithGetter"

export type IAsyncCallback<Args extends any[]> = {
  (...args: Args): Promise<void>
  calling: boolean
  running: boolean
  error: { message: string; error: unknown } | null
}

export function useAsyncCallback<Args extends any[]>(
  callback: (...args: Args) => Promise<void>,
  opts?: { runningTimeoutMs?: number; willHandleError?: boolean },
): IAsyncCallback<Args> {
  const [running, setRunning] = useState(false)
  const [_, setCalling, getCalling] = useStateWithGetter(false)
  const [error, setError] = useState<{ message: string; error: unknown } | null>(null)
  const throwError = useThrowError()

  async function wrapped(...args: Args) {
    if (getCalling()) {
      return
    }
    setCalling(true)

    let timeout: any
    try {
      if (opts?.runningTimeoutMs) {
        timeout = setTimeout(() => setRunning(true), opts.runningTimeoutMs)
      } else {
        setRunning(true)
      }
      await callback(...args)
    } catch (error) {
      if (opts?.willHandleError) {
        setError({ message: messageFromError(error), error })
      } else {
        throwError(error)
      }
    } finally {
      clearTimeout(timeout)
      setRunning(false)
      setCalling(false)
    }
  }

  wrapped.running = running
  wrapped.calling = getCalling()
  wrapped.error = error
  return wrapped
}

function useThrowError() {
  const [error, setError] = useState<unknown>(null)
  if (error) {
    throw error
  }
  return (error: unknown) => setError(error)
}
